'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { signIn, useSession } from 'next-auth/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ProviderIcon } from '@/components/ProviderIcon';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';
import { getAuthInfo, saveAuthInfo } from '@/apis/ApiUtil';
import { useUserLogged } from '@/hooks/useUserLogged';
import type AuthPayload from '@/types/authPayloadType';

type GitProvider = 'github' | 'gitlab' | 'bitbucket';

type StoredIntegrationAuth = {
  provider: GitProvider;
  accessToken?: string;
  refreshToken?: string;
  githubUserName?: string;
  gitlabUserName?: string;
  bitbucketUserName?: string;
};

const PROVIDERS: Array<{ provider: GitProvider; label: string }> = [
  { provider: 'github', label: 'GitHub' },
  { provider: 'gitlab', label: 'GitLab' },
  { provider: 'bitbucket', label: 'Bitbucket' },
];

function normalizeSessionProvider(provider?: string): GitProvider | null {
  const p = provider?.toLowerCase()?.trim();
  if (!p) return null;
  if (p === 'github') return 'github';
  if (p === 'bitbucket') return 'bitbucket';
  // GitLab includes self-hosted providers (e.g. gitlab-self-hosted, gitlab-<slug>)
  if (p === 'gitlab' || p.startsWith('gitlab')) return 'gitlab';
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
}

function integrationToStoredAuth(
  provider: GitProvider,
  integration: unknown
): StoredIntegrationAuth | null {
  if (!isRecord(integration)) return null;

  const accessToken = asString(integration.AccessToken);
  const refreshToken = asString(integration.RefreshToken);

  const githubUserName = asString(integration.GithubUserName);
  const gitlabUserName = asString(integration.GitlabUserName);
  const bitbucketUserName = asString(integration.BitbucketUserName);

  // We consider the integration "present" if we have an access token (refresh
  // token may be omitted depending on what backend returns).
  if (
    !accessToken &&
    !githubUserName &&
    !gitlabUserName &&
    !bitbucketUserName
  ) {
    return null;
  }

  return {
    provider,
    accessToken,
    refreshToken,
    githubUserName,
    gitlabUserName,
    bitbucketUserName,
  };
}

function buildSaveAuthPayload(args: {
  provider: GitProvider;
  accessToken: string;
  userUUID?: string;
  orgUUID?: string;
  sessionProvider?: string;
  sessionUsername?: string | undefined;
  sessionRefreshToken?: string | undefined;
  storedAuth?: StoredIntegrationAuth;
}): AuthPayload {
  const {
    provider,
    accessToken,
    userUUID,
    orgUUID,
    sessionProvider,
    sessionUsername,
    sessionRefreshToken,
    storedAuth,
  } = args;

  const payload: AuthPayload = {
    AccessToken: accessToken,
    IntegrationName: provider,
    UserUUID: userUUID,
    OrgUUID: orgUUID,
    RefreshToken:
      provider === sessionProvider
        ? sessionRefreshToken
        : storedAuth?.refreshToken,
  };

  const usernameFromStored =
    provider === 'github'
      ? storedAuth?.githubUserName
      : provider === 'gitlab'
        ? storedAuth?.gitlabUserName
        : storedAuth?.bitbucketUserName;

  const usernameToUse = sessionUsername ?? usernameFromStored;

  if (provider === 'github') payload.GithubUserName = usernameToUse;
  if (provider === 'gitlab') payload.GitlabUserName = usernameToUse;
  if (provider === 'bitbucket') payload.BitbucketUserName = usernameToUse;

  return payload;
}

export default function GitProviderTokenRefreshCard() {
  const toast = useToast();
  const { user, organization, status: userLoggedStatus } = useUserLogged();
  const { data: session } = useSession() as {
    data: {
      user?: {
        provider?: string;
        username?: string;
        accessToken?: string;
        refreshToken?: string;
      };
    } | null;
  };

  const userUUID = user?.UserUUID;
  const orgUUID = organization?.OrgUUID ?? user?.OrganizationUUID;

  const [storedByProvider, setStoredByProvider] = useState<
    Partial<Record<GitProvider, StoredIntegrationAuth>>
  >({});

  const [isSaving, setIsSaving] = useState<
    Partial<Record<GitProvider, boolean>>
  >({});

  const sessionProviderRaw = session?.user?.provider;
  const sessionProvider =
    normalizeSessionProvider(sessionProviderRaw) ?? undefined;
  const sessionUsername = session?.user?.username;
  const sessionAccessToken = session?.user?.accessToken;
  const sessionRefreshToken = session?.user?.refreshToken;

  const isEnabled = userLoggedStatus === 'authenticated' && !!userUUID;

  const visibleProviders = useMemo(() => {
    if (!sessionProvider) return [];
    const match = PROVIDERS.find((p) => p.provider === sessionProvider);
    return match ? [match] : [];
  }, [sessionProvider]);

  const sessionProviderLabel =
    (sessionProvider &&
      PROVIDERS.find((p) => p.provider === sessionProvider)?.label) ||
    null;

  const loadStoredAuth = useCallback(async () => {
    if (!isEnabled || !userUUID) return;
    if (!sessionProvider) return;

    try {
      const results = await Promise.allSettled(
        visibleProviders.map(({ provider }) =>
          getAuthInfo(userUUID, provider, orgUUID).catch(() => ({
            Error: 'Failed to fetch auth info',
          }))
        )
      );

      const next: Partial<Record<GitProvider, StoredIntegrationAuth>> = {};

      results.forEach((r, idx) => {
        const provider = visibleProviders[idx]?.provider;
        if (!provider) return;
        if (r.status !== 'fulfilled') return;

        const data = r.value as unknown;
        if (!isRecord(data) || typeof data.Error === 'string') {
          return;
        }

        const integrationsUnknown = isRecord(data)
          ? data.Integrations
          : undefined;
        const integrations = Array.isArray(integrationsUnknown)
          ? integrationsUnknown
          : [];

        const firstIntegration = integrations[0];
        const parsed = integrationToStoredAuth(provider, firstIntegration);
        if (parsed) next[provider] = parsed;
      });

      setStoredByProvider(next);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load stored provider auth info.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [isEnabled, orgUUID, sessionProvider, toast, userUUID, visibleProviders]);

  useEffect(() => {
    void loadStoredAuth();
  }, [loadStoredAuth]);

  const statusBadgeVariant = (
    accessToken?: string
  ): 'success' | 'secondary' => {
    return accessToken ? 'success' : 'secondary';
  };

  const getStatusText = (accessToken?: string): string => {
    return accessToken ? 'Connected' : 'Disconnected';
  };

  const canUseSessionToken = useMemo(() => {
    if (!sessionProvider) return new Set<GitProvider>();
    return new Set<GitProvider>([sessionProvider]);
  }, [sessionProvider]);

  const getCallbackUrl = () => {
    if (typeof window === 'undefined') return '/connectors';
    return window.location.pathname + window.location.search;
  };

  const handleReconnectViaNextAuth = useCallback(
    async (provider: GitProvider) => {
      try {
        await signIn(
          provider,
          { redirect: true, callbackUrl: getCallbackUrl() },
          { prompt: 'consent' }
        );
      } catch {
        toast({
          title: 'Error',
          description: `Failed to start ${provider} authentication.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [toast]
  );

  const handleSyncCurrentSession = useCallback(
    async (provider: GitProvider) => {
      if (!userUUID) return;
      if (!canUseSessionToken.has(provider) || !sessionAccessToken) {
        toast({
          title: 'Not available',
          description: `You're not currently signed in with ${provider}. Click Connect/Reconnect instead.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (isSaving[provider]) return;

      setIsSaving((prev) => ({ ...prev, [provider]: true }));
      try {
        const storedAuth = storedByProvider[provider];

        const payload = buildSaveAuthPayload({
          provider,
          accessToken: sessionAccessToken,
          userUUID,
          orgUUID,
          sessionProvider,
          sessionUsername,
          sessionRefreshToken,
          storedAuth,
        });

        const response = await saveAuthInfo(payload);

        if (response?.Error) {
          throw new Error(response?.Error ?? 'Failed to save auth info');
        }

        toast({
          title: 'Synced',
          description: `${PROVIDERS.find((p) => p.provider === provider)?.label ?? provider} session saved.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        // Refresh the UI so the integration table reflects the new token.
        await loadStoredAuth();
      } catch (error) {
        const msg =
          error instanceof Error ? error.message : 'Failed to save token';
        toast({
          title: 'Error',
          description: msg,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsSaving((prev) => ({ ...prev, [provider]: false }));
      }
    },
    [
      isSaving,
      loadStoredAuth,
      orgUUID,
      sessionProvider,
      sessionRefreshToken,
      sessionUsername,
      storedByProvider,
      toast,
      canUseSessionToken,
      sessionAccessToken,
      userUUID,
    ]
  );

  if (!isEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {sessionProviderLabel ?? 'Git provider'} connection
          </CardTitle>
          <CardDescription>
            Sign in to refresh and save your{' '}
            {sessionProviderLabel ?? 'git provider'} connection.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!sessionProvider) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Git provider connection</CardTitle>
          <CardDescription>
            No git provider is associated with the current session.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-4">
        <CardTitle>
          {sessionProviderLabel ?? 'Git provider'} connection
        </CardTitle>
        <CardDescription>
          Reconnect to grant fresh access, then sync so Entelligence uses it.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {/* One provider per session, so this is a single connection row rather
            than a table: a three-column header over one row was all chrome. */}
        {visibleProviders.map(({ provider, label }) => {
          const stored = storedByProvider[provider];
          const isConnected = Boolean(stored?.accessToken);
          const isActiveProvider = canUseSessionToken.has(provider);
          const account =
            sessionUsername ??
            stored?.githubUserName ??
            stored?.gitlabUserName ??
            stored?.bitbucketUserName;

          return (
            <div
              key={provider}
              className="flex items-center gap-4 rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-4"
            >
              <ProviderIcon
                provider={sessionProviderRaw ?? provider}
                className="size-6 shrink-0"
              />

              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="ds-text-body-sm-semibold text-ds-text-darker">
                    {label}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'size-1.5 shrink-0 rounded-full',
                        isConnected
                          ? 'bg-ds-success-default'
                          : 'bg-ds-gray-subtler'
                      )}
                      aria-hidden
                    />
                    <span
                      className={cn(
                        'ds-text-body-xs-medium',
                        isConnected
                          ? 'text-ds-success-default'
                          : 'text-ds-text-subtle'
                      )}
                    >
                      {getStatusText(stored?.accessToken)}
                    </span>
                  </span>
                </div>
                <span className="ds-text-code-sm truncate text-ds-text-subtle">
                  {account ?? 'No account on record'}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleReconnectViaNextAuth(provider)}
                  disabled={isSaving[provider]}
                  className="motion-tap"
                >
                  {isConnected ? 'Reconnect' : 'Connect'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void handleSyncCurrentSession(provider)}
                  disabled={!isActiveProvider || isSaving[provider]}
                  className="motion-tap"
                >
                  {isSaving[provider] ? 'Syncing…' : 'Sync session'}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
